import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Navigation, Loader2 } from "lucide-react";

// Fix default marker icons (CRA/webpack can't resolve Leaflet's relative image paths)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function isValidCoord(lat, lng) {
  return (
    typeof lat === "number" && typeof lng === "number" &&
    !Number.isNaN(lat) && !Number.isNaN(lng) &&
    lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 &&
    // Treat the 0,0 "null island" placeholder as no coordinate.
    !(Math.abs(lat) < 0.0001 && Math.abs(lng) < 0.0001)
  );
}

// Ensure Leaflet tiles render once the container is sized/visible.
function InvalidateOnMount() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 200);
    return () => clearTimeout(t);
  }, [map]);
  return null;
}

export default function PropertyMap({ latitude, longitude, address, addressParts }) {
  const rawLat = latitude === null || latitude === undefined || latitude === "" ? null : Number(latitude);
  const rawLng = longitude === null || longitude === undefined || longitude === "" ? null : Number(longitude);
  const hasDbCoord = isValidCoord(rawLat, rawLng);

  const [geoCoord, setGeoCoord] = useState(null); // {lat,lng} resolved from address fallback
  const [geoState, setGeoState] = useState("idle"); // idle | loading | done | failed

  const queryChain = useMemo(() => {
    const p = addressParts || {};
    const candidates = [
      [address, p.subdistrict_name, p.district_name, p.city_name, p.province_name, "Indonesia"],
      [address, p.district_name, p.city_name, p.province_name, "Indonesia"],
      [address, p.city_name, p.province_name, "Indonesia"],
      [p.district_name, p.city_name, p.province_name, "Indonesia"],
      [p.city_name, p.province_name, "Indonesia"],
    ].map((parts) => parts.filter(Boolean).join(", "));
    return [...new Set(candidates)].filter((q) => q && q.replace(/,|\s|Indonesia/g, "").length > 0);
  }, [address, addressParts]);

  // Fallback: geocode by address when DB coordinates are missing/invalid.
  useEffect(() => {
    if (hasDbCoord || queryChain.length === 0) return;
    let settled = false;
    setGeoState("loading");
    const finish = (state, coord) => {
      if (settled) return;
      settled = true;
      if (coord) setGeoCoord(coord);
      setGeoState(state);
    };
    // Watchdog: never hang on the map — if geocoding is too slow, show the
    // "not available" message instead of an endless spinner.
    const watchdog = setTimeout(() => finish("failed"), 9000);
    (async () => {
      const fetchWithTimeout = async (url, ms = 6000) => {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), ms);
        try {
          const res = await fetch(url, { headers: { Accept: "application/json" }, signal: ctrl.signal });
          return await res.json();
        } finally {
          clearTimeout(t);
        }
      };
      for (const q of queryChain) {
        if (settled) return;
        const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=id&q=${encodeURIComponent(q)}`;
        let data = null;
        try {
          // eslint-disable-next-line no-await-in-loop
          data = await fetchWithTimeout(url);
        } catch {
          data = null;
        }
        if (Array.isArray(data) && data.length > 0) {
          finish("done", { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
          return;
        }
      }
      finish("failed");
    })();
    return () => { settled = true; clearTimeout(watchdog); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDbCoord, queryChain]);

  const coord = hasDbCoord ? { lat: rawLat, lng: rawLng } : geoCoord;
  const approx = !hasDbCoord && !!geoCoord;

  return (
    <div className="space-y-3" data-testid="property-map">
      {address && (
        <p className="text-sm text-foreground flex items-start gap-2" data-testid="property-map-address">
          <MapPin size={16} className="text-accent shrink-0 mt-0.5" />
          <span>{address}</span>
        </p>
      )}

      {coord ? (
        <>
          <div className="h-[280px] sm:h-[380px] w-full overflow-hidden rounded-xl border border-border" data-testid="property-map-canvas">
            <MapContainer center={[coord.lat, coord.lng]} zoom={16} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <InvalidateOnMount />
              <Marker position={[coord.lat, coord.lng]}>
                <Popup>{address || "Lokasi properti"}</Popup>
              </Marker>
            </MapContainer>
          </div>

          {approx && (
            <p className="text-xs text-muted-foreground" data-testid="property-map-approx">
              Titik lokasi merupakan perkiraan berdasarkan alamat.
            </p>
          )}

          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${coord.lat},${coord.lng}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="property-map-directions"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:scale-[1.02] active:scale-95 transition-transform"
          >
            <Navigation size={15} /> Dapatkan Rute
          </a>
        </>
      ) : geoState === "loading" ? (
        <div className="h-[280px] sm:h-[380px] w-full grid place-items-center rounded-xl border border-dashed border-border bg-secondary/20" data-testid="property-map-loading">
          <span className="inline-flex items-center gap-2 text-sm text-muted-foreground"><Loader2 size={16} className="animate-spin" /> Memuat lokasi…</span>
        </div>
      ) : (
        <div className="h-[280px] sm:h-[380px] w-full grid place-items-center rounded-xl border border-dashed border-border bg-secondary/20 text-center px-6" data-testid="property-map-unavailable">
          <div>
            <MapPin size={26} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">Lokasi peta belum tersedia untuk hunian ini.</p>
          </div>
        </div>
      )}
    </div>
  );
}
